import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { LegalLayout, Section } from "../components/LegalLayout";
import { useAuth } from "../lib/auth";
import { sendSupport } from "../lib/api";

const EMAIL = "goldenknee@gmail.com";

function ContactForm() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await sendSupport(form);
      setSent(true);
    } catch (err) {
      toast.error(err.message || "Could not send your message");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return <p data-testid="support-sent" style={{ color: "var(--brand-2)" }}>Thanks! Your message was sent. We'll reply to {form.email} within 1–2 business days.</p>;
  }
  const field = { width: "100%", marginTop: 10 };
  return (
    <form onSubmit={submit} data-testid="support-form">
      <input className="input" style={field} placeholder="Your name (optional)" value={form.name} onChange={set("name")} maxLength={100} data-testid="support-name" />
      <input className="input" style={field} type="email" required placeholder="Your email" value={form.email} onChange={set("email")} data-testid="support-email" />
      <textarea className="input" style={{ ...field, minHeight: 130, resize: "vertical" }} required minLength={5} maxLength={4000} placeholder="How can we help?" value={form.message} onChange={set("message")} data-testid="support-message" />
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 12 }} data-testid="support-submit">
        {busy ? <Loader2 size={16} className="spin" /> : <><Send size={16} /> Send message</>}
      </button>
    </form>
  );
}

export default function Support() {
  const navigate = useNavigate();
  return (
    <LegalLayout title="Support" updated="June 2026" testid="support-page" eyebrow="Help">
      <Section heading="Send us a message">
        <ContactForm />
      </Section>

      <Section heading="Or email us">
        Questions, billing issues, or feedback? Email us and we'll reply within 1–2 business days.
        <div style={{ marginTop: 14 }}>
          <a className="btn btn-primary" href={`mailto:${EMAIL}?subject=NarcCheck%20AI%20Support`} data-testid="support-email-btn">
            <Mail size={16} /> {EMAIL}
          </a>
        </div>
        <p style={{ marginTop: 12, fontSize: 13.5 }}>
          Please include the email you signed up with and, for billing questions, the date of the charge.
        </p>
      </Section>

      <Section heading="How do I cancel my subscription?">
        Go to <b>Settings → Subscription → Manage</b> to open the secure Stripe billing portal and cancel
        anytime. You keep access until the end of your current paid period. If you subscribed in the iOS
        app, cancel in your iPhone's <b>Settings → Apple ID → Subscriptions</b>.
      </Section>

      <Section heading="How does the 3-day free trial work?">
        The Yearly plan ($60/year) starts with a 3-day free trial with $0 due today. If you don't cancel
        before the trial ends, you're charged $60 and the plan renews yearly. The Monthly plan ($8.99/month)
        has no trial and is charged right away.
      </Section>

      <Section heading="Can I get a refund?">
        Except where required by law, payments are non-refundable (see our Terms). If you were charged by
        mistake or something went wrong, email us and we'll review it. iOS App Store purchases are refunded
        through Apple at reportaproblem.apple.com.
      </Section>

      <Section heading="I can't sign in or I forgot my password">
        Use <span className="link" onClick={() => navigate("/forgot-password")} data-testid="support-forgot-link">Forgot password</span> on
        the sign-in page to reset it. If you signed up with Google, use "Continue with Google". Still stuck?
        Email us.
      </Section>

      <Section heading="Is my conversation data private?">
        Your conversations are used only to generate your analysis and are never sold. Saved results stay in
        your History until you delete them. Read the full <span className="link" onClick={() => navigate("/privacy")} data-testid="support-privacy-link">Privacy Policy</span>.
      </Section>

      <Section heading="How do I delete my account?">
        Go to <b>Settings → Delete account</b>. This permanently removes your account and saved history.
        Cancel any active subscription first so you aren't billed again.
      </Section>

      <Section heading="Need immediate help?">
        NarcCheck AI is for personal reflection only and is not professional, medical, or legal advice. If you
        feel unsafe, contact local emergency services. In the US, call or text <b>988</b> (Suicide & Crisis
        Lifeline) or call the National Domestic Violence Hotline at <b>1-800-799-7233</b>.
      </Section>

      <p style={{ fontSize: 13.5 }}>
        <span className="link" onClick={() => navigate("/terms")} data-testid="support-terms-link">Terms of Service</span>
        {" · "}
        <span className="link" onClick={() => navigate("/privacy")}>Privacy Policy</span>
      </p>
    </LegalLayout>
  );
}
